import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SignUpFlow from "../../Container/Auth/Layout";
import { EmailIdentificationWithTheme, SignUpOTPWithTheme, loadRecaptcha } from "../../api";
import ButtonLoader from "../Loader";
import { useAuth } from "../../hooks";
import { OTPInput, useResendTimer } from "../OtpInput/OtpInput";

function OTPPage() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";
  const navigate = useNavigate();

  const [pin, setPin] = useState(Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    setPassword,
    setConfirmPass,
    setUserName,
    setFormData,
    signupOtpToken,
    setSignupOtpToken,
    setSignupOtp,
  } = useAuth();

  useEffect(() => {
    loadRecaptcha();
  }, []);

  useEffect(() => {
    if (!initialEmail) {
      navigate("/");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use the timer hook
  const { timeLeft, resetTimer, isPending, setIsPending, TimerDisplay } =
    useResendTimer(180);

  const handlePinChange = (newPin) => {
    setPin(newPin);
    setError("");
  };

  const handleVerifyOTP = async () => {
    const otp = pin.join("");
    if (otp.length < 6) {
      setError("Please enter the complete OTP.");
      return;
    }

    try {
      setLoading(true);
      const formattedOTP = {
        otp: otp,
        email: initialEmail,
        otpToken: signupOtpToken,
      };

      // Clear session data
      sessionStorage.clear();
      setFormData({});

      const signUpData = await SignUpOTPWithTheme(formattedOTP);

      if (signUpData?.status === 200) {
        setSignupOtp(otp);
        // Reset form states
        setError("");
        setPassword("");
        setConfirmPass("");
        setUserName("");

        // Navigate to next step
        navigate(`/create-user/?email=${encodeURIComponent(initialEmail)}`, {
          replace: true,
        });
      }
    } catch (error) {
      setError(error?.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsPending(true);
      setPin(Array(6).fill(""));
      setError("");

      // Request new OTP
      const EmailExist = await EmailIdentificationWithTheme(initialEmail);

      if (EmailExist?.data?.otpToken) {
        setSignupOtpToken(EmailExist.data.otpToken);
        resetTimer();
      }
    } catch (error) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <SignUpFlow headerText={"Verify your email"}>
      <p className="font-geomanistRegular text-[14px] text-greyish">
        Enter the 6-digit OTP sent to your email address
      </p>

      <div className="mb-[27px] flex items-center gap-2 leading-[20px]">
        <span className="text-blackShade font-geomanistBold text-[14px]">
          {initialEmail}
        </span>
        <button
          className="text-blueish text-[14px] hover:underline"
          onClick={() =>
            navigate(`/email?email=${encodeURIComponent(initialEmail)}`)
          }
          aria-label="Change email address"
        >
          Edit
        </button>
      </div>

      <div className="mb-2">
        <OTPInput
          length={6}
          initialValue={pin}
          onChange={handlePinChange}
          hasError={!!error}
          disabled={loading || isPending}
        />
      </div>

      {error && <p className="text-red-500 text-[14px] mt-1">{error}</p>}

      <TimerDisplay />

      <button
        className="w-full flex flex-row gap-2 justify-center mb-[28px] text-white py-2 px-4 rounded-md bg-blueish font-geomanistMedium leading-[23px] text-[18px] hover:pointer disabled:bg-blueish/50"
        onClick={handleVerifyOTP}
        disabled={!pin.every((digit) => digit !== "") || loading || isPending}
        aria-label="Verify OTP"
      >
        {loading && <ButtonLoader />} Verify OTP
      </button>

      {timeLeft === 0 && (
        <button
          className="w-full text-blueish text-center underline"
          onClick={handleResendOTP}
          disabled={loading || isPending || timeLeft > 0}
          aria-label="Resend OTP"
        >
          Resend OTP
        </button>
      )}
    </SignUpFlow>
  );
}

export default OTPPage;
